import { apiClient } from './api';
import type {
  Annotation,
  AnnotationRequest,
  AnnotationResponse,
  SpatialGoal,
  TimingConfig,
  AxisConfig,
  JointAngleConfig,
} from '@/types/api';
import type { TrajectoryConfig } from '@/types/trajectory';

/**
 * Hull data returned from backend
 */
export interface HullCalculationResponse {
  success: boolean;
  annotation_id: string;
  total_frames: number;
  hulls_calculated: number;
  annotation: {
    id: string;
    landmark_indices: number[];
    convex_hulls: Record<string, number[][]>;
    smoothing?: {
      enabled: boolean;
      window_size: number;
      method: string;
    };
  };
}

export interface HullDataResponse {
  annotation_id: string;
  hulls: Record<string, number[][]>;
  smoothing?: {
    enabled: boolean;
    window_size: number;
    method: string;
  };
}

/**
 * Create or update annotations for a video (batch)
 */
export async function saveAnnotations(
  request: AnnotationRequest
): Promise<AnnotationResponse> {
  const response = await apiClient.post<AnnotationResponse>(
    `/annotations/${request.video_id}`,
    request
  );
  return response.data;
}

/**
 * Request payload for saveAnnotation(). Built ad hoc at each RegionAnnotator.tsx
 * call site (one per goal type — spatial/movement/timing/axis/joint-angle);
 * the goal-specific fields are mutually exclusive per call, so all are optional.
 */
export interface SaveAnnotationPayload {
  id: string;
  type: 'rectangle' | 'circle' | 'line';
  coordinates: {
    x: number;
    y: number;
    width?: number;
    height?: number;
    radius?: number;
    x2?: number;
    y2?: number;
  };
  importance: 'low' | 'medium' | 'high' | 'critical';
  emphasized_landmarks: string[];
  condition_text?: string;
  description?: string;
  timestamp?: number;
  target_phase?: string | null;
  active_frames?: {
    mode: 'all' | 'range';
    startFrame?: number;
    endFrame?: number;
  };
  functional_region?: string;
  spatial_goal?: SpatialGoal;
  movement_goal?: TrajectoryConfig;
  timing_goal?: TimingConfig;
  axis_goal?: AxisConfig;
  joint_angle_goal?: JointAngleConfig;
}

/** Response shape for saveAnnotation() — backend echoes the saved annotation. */
export interface SaveAnnotationResponse {
  id?: string;
  emphasized_landmarks?: string[];
  [key: string]: unknown;
}

/**
 * Save a single annotation for a video
 * This is used by RegionAnnotator for individual annotation saves
 */
export async function saveAnnotation(
  videoId: string,
  annotation: SaveAnnotationPayload
): Promise<SaveAnnotationResponse> {
  const response = await apiClient.post<SaveAnnotationResponse>(
    `/annotations/${videoId}`,
    annotation
  );
  return response.data;
}

/**
 * Get annotations for a video
 */
export async function getAnnotations(videoId: string): Promise<Annotation> {
  const response = await apiClient.get<Annotation>(`/annotations/${videoId}`);
  return response.data;
}

/**
 * Delete annotations for a video
 */
export async function deleteAnnotations(videoId: string): Promise<void> {
  await apiClient.delete(`/annotations/${videoId}`);
}

/**
 * Check if video has annotations
 */
export async function hasAnnotations(videoId: string): Promise<boolean> {
  try {
    await getAnnotations(videoId);
    return true;
  } catch {
    return false;
  }
}

/**
 * Calculate convex hulls for a tracked annotation
 *
 * This triggers the backend to calculate smooth convex hull boundaries
 * around the landmarks associated with an annotation across all video frames.
 *
 * @param videoId - Video identifier (filename)
 * @param annotationId - Annotation identifier
 * @param smoothingWindow - Temporal smoothing window size (3-10, default 5)
 * @returns Hull calculation result with convex_hulls data
 */
export async function calculateRegionHulls(
  videoId: string,
  annotationId: string,
  smoothingWindow: number = 5
): Promise<HullCalculationResponse> {
  const response = await apiClient.post<HullCalculationResponse>(
    `/annotations/${videoId}/${annotationId}/calculate-hulls`,
    null,
    {
      params: { smoothing_window: smoothingWindow }
    }
  );
  return response.data;
}

/**
 * Get pre-calculated hull data for an annotation
 *
 * Retrieves the convex hull data if it has been previously calculated.
 * If hulls haven't been calculated yet, this will throw a 400 error.
 *
 * @param videoId - Video identifier (filename)
 * @param annotationId - Annotation identifier
 * @returns Hull data with frame-by-frame polygon vertices
 */
export async function getRegionHulls(
  videoId: string,
  annotationId: string
): Promise<HullDataResponse> {
  const response = await apiClient.get<HullDataResponse>(
    `/annotations/${videoId}/${annotationId}/hulls`
  );
  return response.data;
}

/**
 * Predefined region interface
 *
 * IMPORTANT: This matches the backend API response structure exactly.
 * Backend returns 'landmarks' not 'landmark_indices'.
 */
export interface PredefinedRegion {
  id: string;
  name: string;
  description: string;
  landmarks: number[];  // Backend returns 'landmarks', not 'landmark_indices'
  landmark_names: string[];
  color: string;  // RGBA color string from backend
  functional?: boolean; // True for Lead/Trail labels, false for Left/Right anatomical labels
  default_handedness?: 'right' | 'left';  // Default handedness for functional regions

  // Frontend-only computed properties (with defaults for backward compatibility)
  default_importance?: 'low' | 'medium' | 'high' | 'critical';
  region_type?: string;
  side?: 'left' | 'right' | 'bilateral';
}

/**
 * Get list of all predefined body regions
 *
 * Returns a curated list of anatomical regions that can be quickly selected
 * when creating annotations. These regions have pre-defined landmark sets.
 *
 * @returns List of predefined regions with metadata
 */
export async function getPredefinedRegions(): Promise<PredefinedRegion[]> {
  const response = await apiClient.get<PredefinedRegion[]>('/regions/predefined');
  return response.data;
}

/**
 * Get details of a specific predefined region
 *
 * @param regionName - Name/ID of the predefined region
 * @returns Detailed region information
 */
export async function getPredefinedRegion(regionName: string): Promise<PredefinedRegion> {
  const response = await apiClient.get<PredefinedRegion>(`/regions/predefined/${regionName}`);
  return response.data;
}

/**
 * Get a resolved functional region with auto-detected lead side
 *
 * For functional regions (Lead Arm, Trail Leg, etc.), the backend auto-detects
 * the lead side using motion-extent analysis of the video's pose data. This
 * works with helmets, masks, and any face-occluding equipment.
 *
 * @param regionName - Name/ID of the predefined region (e.g., "lead_arm", "trail_leg")
 * @param videoId - Video ID for lead side auto-detection from pose data
 * @param leadSideOverride - Optional manual override ("left" or "right") for lead side
 * @returns Resolved region with correct landmarks based on detected lead side
 */
export async function getResolvedRegion(
  regionName: string,
  videoId: string,
  leadSideOverride?: 'left' | 'right'
): Promise<PredefinedRegion> {
  const params: Record<string, string> = { video_id: videoId };
  if (leadSideOverride) {
    params.facing_override = leadSideOverride;
  }
  const response = await apiClient.get<PredefinedRegion>(
    `/regions/predefined/${regionName}`,
    { params }
  );
  return response.data;
}
